import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import AdminDashboardModal from "../adminDashboard/adminDashboardModal";
import ReactApexChart from "react-apexcharts";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { fetchTeacherDashboardApi } from "../../../core/data/services/teacherDashboardService";
import {
  setError,
} from "../../../core/data/redux/teacherDashboardSlice";
import { RootState } from "../../../core/data/redux/store";
import { getUserDetails } from "../../../lib/getUserDetails";

const MealSupervisorDashboard = () => {
  const routes = all_routes;
  const dispatch = useDispatch();

  // Get user data
  const user = useSelector((state: RootState) => state.user);

  const [mealsDateRange, setMealsDateRange] = useState<"today"|"thisWeek"|"lastWeek"|"lastMonth"|"custom">("today");
  const [calendarDate, setCalendarDate] = useState<Nullable<Date>>(null);

  // Custom date range states
  const [customMealsStartDate, setCustomMealsStartDate] = useState<Nullable<Date>>(null);
  const [customMealsEndDate, setCustomMealsEndDate] = useState<Nullable<Date>>(null);
  const [globalCustomStartDate, setGlobalCustomStartDate] = useState<Nullable<Date>>(null);
  const [globalCustomEndDate, setGlobalCustomEndDate] = useState<Nullable<Date>>(null);

  // Global filter states
  const [isGlobalFilterActive, setIsGlobalFilterActive] = useState(false);
  const [showGlobalDateFilter, setShowGlobalDateFilter] = useState(false);

  // Data states
  const [mealsData, setMealsData] = useState({ mealsServedCount: 0, melasNotServedCount: 0 });
  const [classWiseData, setClassWiseData] = useState<any[]>([]);
  const [, setCumulativeData] = useState<any>({});
  const [mealsLoading, setMealsLoading] = useState(false);
  const [mealsDropdownOpen, setMealsDropdownOpen] = useState(false);

  const userData = getUserDetails();
  const schoolId = userData?.[0]?.relatedEntity?.schools?.id;

  // Class-wise meals chart
  const [classMealsChart, setClassMealsChart] = useState<any>({
    chart: {
      height: 300,
      type: 'bar',
      stacked: true,
      toolbar: { show: false }
    },
    legend: {
      show: true,
      horizontalAlign: 'left',
      position: 'top',
      fontSize: '14px',
      labels: { colors: '#5D6369' }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        endingShape: 'rounded'
      },
    },
    colors: ['#1ABE17', '#E82646', '#FFA500'],
    dataLabels: { enabled: false },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    grid: { padding: { left: -8 } },
    series: [{
      name: 'Meals Served',
      data: []
    }, {
      name: 'Meals Not Served',
      data: []
    }, {
      name: 'Total Students',
      data: []
    }],
    xaxis: { categories: [] },
    yaxis: {
      tickAmount: 3,
      labels: { offsetX: -15 }
    },
    fill: { opacity: 1 },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val + " students"
        }
      }
    }
  });

  // Helper function for exact proportional distribution
  const distributeWithLargestRemainder = (proportions: number[], total: number): number[] => {
    const floors = proportions.map(p => Math.floor(p));
    const remainders = proportions.map((p, i) => ({ index: i, remainder: p - floors[i] }));
    
    // Sort by remainder in descending order
    remainders.sort((a, b) => b.remainder - a.remainder);
    
    const distributed = [...floors];
    const currentSum = floors.reduce((sum, val) => sum + val, 0);
    const difference = total - currentSum;
    
    // Add 1 to the classes with largest remainders
    for (let i = 0; i < difference; i++) {
      if (i < remainders.length) {
        distributed[remainders[i].index] += 1;
      }
    }
    
    return distributed;
  };

  const getDatesByRange = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
    const today = dayjs();
    
    if (range === "custom" && customStart && customEnd) {
      return {
        startDate: dayjs(customStart).format("YYYY-MM-DD"),
        endDate: dayjs(customEnd).format("YYYY-MM-DD")
      };
    }
    
    switch (range) {
      case "today":
        return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
      case "thisWeek":
        return {
          startDate: today.startOf("week").format("YYYY-MM-DD"),
          endDate: today.endOf("week").format("YYYY-MM-DD"),
        };
      case "lastWeek":
        return {
          startDate: today.subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
          endDate: today.subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
        };
      case "lastMonth":
        return {
          startDate: today.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
          endDate: today.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
        };
      default:
        return { startDate: today.format("YYYY-MM-DD"), endDate: today.format("YYYY-MM-DD") };
    }
  };

  const fetchMealsData = useCallback(async (startDate: string, endDate: string) => {
    try {
      setMealsLoading(true);
      const res = await fetchTeacherDashboardApi({ 
        startDate, 
        endDate, 
        schoolIds: schoolId, 
        isCumulative: true 
      });
    
      if (res && res.cumulative) {
        setMealsData({
          mealsServedCount: res.cumulative.mealsServedCount || 0,
          melasNotServedCount: res.cumulative.melasNotServedCount || 0
        });
        setCumulativeData(res.cumulative);
      }

      if (res && res.studentscount) {
        setClassWiseData(res.studentscount);
      }
    } catch (error: any) {
      dispatch(setError(error.message));
      console.error("Error fetching meals data:", error);
      setMealsData({ mealsServedCount: 0, melasNotServedCount: 0 });
    } finally {
      setMealsLoading(false);
    }
  }, [dispatch, schoolId]);

  // Update class-wise meals chart - FIXED VERSION
  useEffect(() => {
    if (classWiseData && Array.isArray(classWiseData) && mealsData) {
      const categories = classWiseData.map(cls => `${cls.className}-${cls.sectionName}`);
      const totalStudents = classWiseData.map(cls => cls.total);
      
      let mealsServedData: number[] = [];
      let mealsNotServedData: number[] = [];

      // Check if API provides actual class-wise meal data
      const hasActualMealData = classWiseData.some(cls => 
        cls.hasOwnProperty('mealsServed') || cls.hasOwnProperty('mealsNotServed')
      );

      if (hasActualMealData) {
        // Use actual class-wise data from API
        mealsServedData = classWiseData.map(cls => cls.mealsServed || 0);
        mealsNotServedData = classWiseData.map(cls => cls.mealsNotServed || 0);
      } else {
        // Calculate proportional distribution with exact totals
        const totalStudentsCount = classWiseData.reduce((sum, cls) => sum + cls.total, 0);
        
        if (totalStudentsCount === 0) {
          mealsServedData = new Array(classWiseData.length).fill(0);
          mealsNotServedData = new Array(classWiseData.length).fill(0);
        } else {
          // Distribute meals proportionally using largest remainder method for accuracy
          const servedProportions = classWiseData.map(cls => 
            (cls.total / totalStudentsCount) * mealsData.mealsServedCount
          );
          const notServedProportions = classWiseData.map(cls => 
            (cls.total / totalStudentsCount) * mealsData.melasNotServedCount
          );

          // Use largest remainder method for exact distribution
          mealsServedData = distributeWithLargestRemainder(servedProportions, mealsData.mealsServedCount);
          mealsNotServedData = distributeWithLargestRemainder(notServedProportions, mealsData.melasNotServedCount);
        }
      }

      setClassMealsChart((prevChart: any) => ({
        ...prevChart,
        series: [
          {
            name: 'Meals Served',
            data: mealsServedData
          },
          {
            name: 'Meals Not Served',
            data: mealsNotServedData
          },
          {
            name: 'Total Students',
            data: totalStudents
          }
        ],
        xaxis: {
          categories: categories
        }
      }));
    }
  }, [classWiseData, mealsData]);

  // Effects for data fetching
  useEffect(() => {
    if (isGlobalFilterActive || calendarDate) return;
    
    const { startDate, endDate } = getDatesByRange(mealsDateRange, customMealsStartDate, customMealsEndDate);
    fetchMealsData(startDate, endDate);
  }, [mealsDateRange, customMealsStartDate, customMealsEndDate, fetchMealsData, isGlobalFilterActive, calendarDate]);

  useEffect(() => {
    if (!calendarDate) return;
    
    const selectedDate = dayjs(calendarDate).format("YYYY-MM-DD");
    setIsGlobalFilterActive(false);
    fetchMealsData(selectedDate, selectedDate);
  }, [calendarDate, fetchMealsData]);

  useEffect(() => {
    const today = dayjs().format("YYYY-MM-DD");
    fetchMealsData(today, today);
  }, [fetchMealsData]);

  useEffect(() => {
    if (isGlobalFilterActive && globalCustomStartDate && globalCustomEndDate) {
      const startDate = dayjs(globalCustomStartDate).format("YYYY-MM-DD");
      const endDate = dayjs(globalCustomEndDate).format("YYYY-MM-DD");
      fetchMealsData(startDate, endDate);
    }
  }, [isGlobalFilterActive, globalCustomStartDate, globalCustomEndDate, fetchMealsData]);

  // Chart configurations
  const mealsChartOpts: any = {
    chart: { height: 290, type: "donut" as const, toolbar: { show: false } },
    dataLabels: { enabled: false },
    series: [mealsData.mealsServedCount, mealsData.melasNotServedCount],
    labels: ["Served", "Not Served"],
    colors: ["#1ABE17", "#E82646"],
    legend: { position: "bottom" as const },
    plotOptions: {
      pie: {
        donut: {
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              formatter: () => {
                return (mealsData.mealsServedCount + mealsData.melasNotServedCount).toString();
              }
            }
          }
        }
      }
    }
  };

  const displayText = {
    today: "Today",
    thisWeek: "This Week",
    lastWeek: "Last Week",
    lastMonth: "Last Month",
    custom: "Custom Range",
  } as const;

  const handleMealsRangeChange = (range: string) => {
    setMealsDateRange(range as any);
    setCalendarDate(null);
    setIsGlobalFilterActive(false);
    setMealsDropdownOpen(false);
    
    if (range !== "custom") {
      setCustomMealsStartDate(null);
      setCustomMealsEndDate(null);
    }
  };

  const handleCalendarChange = (e: any) => {
    setCalendarDate(e.value);
    setIsGlobalFilterActive(false);
    setGlobalCustomStartDate(null);
    setGlobalCustomEndDate(null);
  };

  const handleGlobalDateFilter = () => {
    if (globalCustomStartDate && globalCustomEndDate) {
      setIsGlobalFilterActive(true);
      setCalendarDate(null);
      setMealsDateRange("today");
      setCustomMealsStartDate(null);
      setCustomMealsEndDate(null);
    }
  };

  const clearGlobalFilter = () => {
    setGlobalCustomStartDate(null);
    setGlobalCustomEndDate(null);
    setIsGlobalFilterActive(false);
    setShowGlobalDateFilter(false);
    setCalendarDate(null);
    setMealsDateRange("today");
    setCustomMealsStartDate(null);
    setCustomMealsEndDate(null);
    
    const today = dayjs().format("YYYY-MM-DD");
    fetchMealsData(today, today);
  };

  const getDisplayText = (range: string, customStart?: Date | null, customEnd?: Date | null) => {
    if (isGlobalFilterActive && globalCustomStartDate && globalCustomEndDate) {
      return `Global Filter: ${dayjs(globalCustomStartDate).format("MMM DD")} - ${dayjs(globalCustomEndDate).format("MMM DD, YYYY")}`;
    }
    
    if (calendarDate) {
      return dayjs(calendarDate).format("MMM DD, YYYY");
    }
    
    if (range === "custom" && customStart && customEnd) {
      return `${dayjs(customStart).format("MMM DD")} - ${dayjs(customEnd).format("MMM DD, YYYY")}`;
    }
    
    return displayText[range as keyof typeof displayText];
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Welcome Header */}
          <div className="card bg-dark mb-4">
            <div className="overlay-img">
              <ImageWithBasePath
                src="assets/img/bg/shape-04.png"
                alt="img"
                className="img-fluid shape-01"
              />
              <ImageWithBasePath
                src="assets/img/bg/shape-01.png"
                alt="img"
                className="img-fluid shape-02"
              />
              <ImageWithBasePath
                src="assets/img/bg/shape-02.png"
                alt="img"
                className="img-fluid shape-03"
              />
              <ImageWithBasePath
                src="assets/img/bg/shape-03.png"
                alt="img"
                className="img-fluid shape-04"
              />
            </div>
            <div className="card-body">
              <div className="d-flex align-items-xl-center justify-content-xl-between flex-xl-row flex-column">
                <div className="mb-3 mb-xl-0">
                  <div className="d-flex align-items-center flex-wrap mb-2">
                    <h1 className="text-white me-2">
                      Welcome Back, {user?.Userdetail?.firstName} {user?.Userdetail?.lastName}
                    </h1>
                    <Link
                      to={routes.userViewProfile}
                      className="avatar avatar-sm img-rounded bg-gray-800 dark-hover"
                    >
                      <i className="ti ti-edit text-white" />
                    </Link>
                  </div>
                  <p className="text-white">Meal Supervisor Dashboard - Monitor and manage school meals efficiently</p>
                </div>
              </div>
            </div>
          </div>

          {/* Header with Global Filter */}
          <div className="d-md-flex align-items-center justify-content-between mb-4">
            <div>
              <h3 className="page-title mb-1">Meal Supervisor Dashboard</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.mealSupervisor}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Meal Management</li>
                </ol>
              </nav>
            </div>
            
            {/* Global Date Filter */}
            <div className="d-flex align-items-center gap-2">
              {!showGlobalDateFilter && (
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowGlobalDateFilter(true)}
                >
                  <i className="ti ti-filter me-1"></i>
                  Global Date Filter
                </button>
              )}
              
              {showGlobalDateFilter && (
                <div className="d-flex align-items-center gap-2 p-3 border rounded bg-light">
                  <small className="text-muted fw-medium text-nowrap">Filter All:</small>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: '140px' }}
                    value={globalCustomStartDate ? dayjs(globalCustomStartDate).format("YYYY-MM-DD") : ""}
                    onChange={(e) => setGlobalCustomStartDate(e.target.value ? new Date(e.target.value) : null)}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  <span className="text-muted">to</span>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    style={{ width: '140px' }}
                    value={globalCustomEndDate ? dayjs(globalCustomEndDate).format("YYYY-MM-DD") : ""}
                    onChange={(e) => setGlobalCustomEndDate(e.target.value ? new Date(e.target.value) : null)}
                    max={new Date().toISOString().split('T')[0]}
                    min={globalCustomStartDate ? dayjs(globalCustomStartDate).format("YYYY-MM-DD") : ""}
                  />
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={handleGlobalDateFilter}
                    disabled={!globalCustomStartDate || !globalCustomEndDate}
                  >
                    Apply
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={clearGlobalFilter}
                  >
                    Clear
                  </button>
                  <button 
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setShowGlobalDateFilter(false)}
                  >
                    <i className="ti ti-x"></i>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Charts Row */}
          <div className="row mb-4">
            {/* Class-wise Meals Distribution */}
            <div className="col-xxl-8 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex align-items-center justify-content-between">
                  <h4 className="card-title">Class-wise Meal Distribution</h4>
                </div>
                <div className="card-body pb-0">
                  {!mealsLoading ? (
                    <ReactApexChart
                      options={classMealsChart}
                      series={classMealsChart.series}
                      type="bar"
                      height={300}
                    />
                  ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '300px'}}>
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Meals Overview Donut Chart */}
            <div className="col-xxl-4 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between">
                  <h4 className="card-title">
                    Meals Overview
                    {mealsLoading && <span className="spinner-border spinner-border-sm ms-2" />}
                  </h4>
                  <div className="card-dropdown">
                    <button 
                      className="btn dropdown-toggle p-0 border-0 bg-transparent" 
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded={mealsDropdownOpen}
                      onClick={() => setMealsDropdownOpen(!mealsDropdownOpen)}
                      disabled={isGlobalFilterActive}
                    >
                      <i className="ti ti-calendar-due" /> 
                      {getDisplayText(mealsDateRange, customMealsStartDate, customMealsEndDate)}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                      {Object.keys(displayText).map(r => (
                        <li key={r}>
                          <button 
                            className="dropdown-item" 
                            type="button"
                            onClick={() => handleMealsRangeChange(r)}
                          >
                            {displayText[r as keyof typeof displayText]}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="card-body">
                  <div className="border rounded p-3 mb-3">
                    <div className="row text-center">
                      <div className="col border-end">
                        <p className="mb-1">Served</p>
                        <h5 className="text-success">{mealsData.mealsServedCount}</h5>
                      </div>
                      <div className="col">
                        <p className="mb-1">Not Served</p>
                        <h5 className="text-danger">{mealsData.melasNotServedCount}</h5>
                      </div>
                    </div>
                  </div>
                  {!mealsLoading ? (
                    <ReactApexChart 
                      options={mealsChartOpts} 
                      series={mealsChartOpts.series} 
                      type="donut" 
                      height={200} 
                    />
                  ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{height: '200px'}}>
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-xxl-4 col-sm-12 d-flex">
              <div className="card flex-fill">
                <div className="card-header d-flex justify-content-between">
                  <h4 className="card-title">Schedules</h4>
                  {calendarDate && (
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setCalendarDate(null)}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="card-body">
                  <Calendar
                    inline
                    value={calendarDate}
                    onChange={handleCalendarChange}
                    className="custom-cal-react"
                    maxDate={new Date()}
                  />
                </div>
              </div>
            </div>

          {/* Custom date range inputs for individual cards when needed */}
          {mealsDateRange === "custom" && !isGlobalFilterActive && !calendarDate && (
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <h5>Custom Date Range for Meals</h5>
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={customMealsStartDate ? dayjs(customMealsStartDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomMealsStartDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={customMealsEndDate ? dayjs(customMealsEndDate).format("YYYY-MM-DD") : ""}
                          onChange={(e) => setCustomMealsEndDate(e.target.value ? new Date(e.target.value) : null)}
                          max={new Date().toISOString().split('T')[0]}
                          min={customMealsStartDate ? dayjs(customMealsStartDate).format("YYYY-MM-DD") : ""}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <AdminDashboardModal />
    </>
  );
};

export default MealSupervisorDashboard;